import { sumBy } from '@markku/utils'
import { z } from 'zod'

export interface ServiceTaskLine {
  taskId: number
  /** Computed price for this booking, in euros. */
  price: number
}

export interface ServiceBookingData {
  tasks: ServiceTaskLine[]
  serviceDetails: string
  estimatedDurationMinutes: number
  estimatedCost: number
}

export type ServicePresetKey = 'nuohous' | 'ilmanvaihto'

export interface ServicePresetData {
  id: string
  name: string
  description: string
  maxDrivingMinutes: number
  minBookingAdvanceHours: number
  slotSpacingMinutes: number
  notificationEmails: string[]
  defaultFormValue: Record<string, unknown>
  getBookingData: (formValue: Record<string, unknown>) => ServiceBookingData
}

// --- Nuohous (chimney sweep) preset data ---

interface NuohousOption {
  fireplaces: number
  taskId: number
  duration: number
  cost: number
}

interface NuohousBoilerOption {
  taskId: number
  duration: number
  cost: number
}

export const nuohousFormValueSchema = z.object({
  fireplaces: z.number().int().min(1).max(5),
  hasBoiler: z.boolean().optional(),
})

export type NuohousFormValue = z.infer<typeof nuohousFormValueSchema>

// Fireplace options: fireplaces, taskId, duration (min), cost (€).
// Task IDs match production — the same IDs are seeded locally via fixtures.
export const NUOHOUS_OPTIONS: NuohousOption[] = [
  { fireplaces: 1, taskId: 1741, duration: 25, cost: 68 },
  { fireplaces: 2, taskId: 54356, duration: 40, cost: 88 },
  { fireplaces: 3, taskId: 54373, duration: 55, cost: 108 },
  { fireplaces: 4, taskId: 54323, duration: 70, cost: 128 },
  { fireplaces: 5, taskId: 54238, duration: 85, cost: 148 },
]

// Boiler sweep add-on: taskId, duration (min), cost (€)
export const NUOHOUS_BOILER_OPTION: NuohousBoilerOption = {
  taskId: 54240,
  duration: 15,
  cost: 25,
}

export function getNuohousBookingData(
  formValue: Record<string, unknown>,
): ServiceBookingData {
  const { fireplaces, hasBoiler = false } = formValue as NuohousFormValue
  const opt =
    NUOHOUS_OPTIONS.find((o) => o.fireplaces === fireplaces) ??
    NUOHOUS_OPTIONS[0]

  const tasks: ServiceTaskLine[] = [{ taskId: opt.taskId, price: opt.cost }]
  let totalDuration = opt.duration

  if (hasBoiler) {
    tasks.push({
      taskId: NUOHOUS_BOILER_OPTION.taskId,
      price: NUOHOUS_BOILER_OPTION.cost,
    })
    totalDuration += NUOHOUS_BOILER_OPTION.duration
  }

  const totalCost = sumBy(tasks, (t) => t.price)
  const fireplaceText = `${opt.fireplaces} tulisija${opt.fireplaces > 1 ? 'a' : ''}`
  const boilerText = hasBoiler ? ' + kattilanuohous' : ''

  return {
    tasks,
    serviceDetails: `${fireplaceText}${boilerText} - ${totalCost} €`,
    estimatedDurationMinutes: totalDuration,
    estimatedCost: totalCost,
  }
}

// --- Ilmanvaihto (ventilation cleaning) preset data ---

export const apartmentSizeSchema = z.enum([
  'under_80',
  '80_to_120',
  '120_to_160',
])
export type ApartmentSize = z.infer<typeof apartmentSizeSchema>

export const floorsSchema = z.enum(['single', 'multiple'])

export const ventilationTypeSchema = z.enum([
  'natural',
  'mechanical_exhaust',
  'heat_recovery',
])
export type VentilationType = z.infer<typeof ventilationTypeSchema>

export const ilmanvaihtoFormValueSchema = z.object({
  apartmentSize: apartmentSizeSchema,
  floors: floorsSchema,
  ventilationType: ventilationTypeSchema,
  includeAirflowMeasurement: z.boolean().optional(),
  includeCookerHoodCleaning: z.boolean().optional(),
})

export type IlmanvaihtoFormValue = z.infer<typeof ilmanvaihtoFormValueSchema>

// Prices by size × ventilation type
export const IV_CLEANING_PRICES: Record<
  ApartmentSize,
  Record<VentilationType, number>
> = {
  under_80: { natural: 300, mechanical_exhaust: 390, heat_recovery: 450 },
  '80_to_120': {
    natural: 350,
    mechanical_exhaust: 450,
    heat_recovery: 520,
  },
  '120_to_160': {
    natural: 400,
    mechanical_exhaust: 490,
    heat_recovery: 590,
  },
}

// Airflow measurement add-on prices (50% of standalone, varies by size)
export const IV_MEASUREMENT_PRICES: Record<ApartmentSize, number> = {
  under_80: 75,
  '80_to_120': 100,
  '120_to_160': 125,
}

export const IV_MULTI_FLOOR_SURCHARGE = 50
export const IV_COOKER_HOOD_PRICE = 60

// Duration estimates (minutes) — full lookup table (from IV expert)
const IV_DURATION: Record<ApartmentSize, Record<VentilationType, number>> = {
  under_80: { natural: 150, mechanical_exhaust: 150, heat_recovery: 180 },
  '80_to_120': { natural: 180, mechanical_exhaust: 180, heat_recovery: 220 },
  '120_to_160': { natural: 180, mechanical_exhaust: 180, heat_recovery: 270 },
}

const IV_DURATION_MULTI_FLOOR = 15
const IV_DURATION_COOKER_HOOD = 30

const IV_DURATION_MEASUREMENT: Record<
  ApartmentSize,
  Record<'mechanical_exhaust' | 'heat_recovery', number>
> = {
  under_80: { mechanical_exhaust: 20, heat_recovery: 45 },
  '80_to_120': { mechanical_exhaust: 40, heat_recovery: 60 },
  '120_to_160': { mechanical_exhaust: 60, heat_recovery: 100 },
}

// Task IDs match production — the same IDs are seeded locally via fixtures.
export const IV_TASK_IDS: Record<
  VentilationType | 'measurement' | 'cookerHood',
  number
> = {
  natural: 60317,
  mechanical_exhaust: 69448,
  heat_recovery: 69042,
  measurement: 69419,
  cookerHood: 82225,
}

const IV_TYPE_LABELS: Record<VentilationType, string> = {
  natural: 'painovoimainen',
  mechanical_exhaust: 'koneellinen poisto',
  heat_recovery: 'tulo- ja poisto (LTO)',
}

const IV_SIZE_LABELS: Record<ApartmentSize, string> = {
  under_80: 'alle 80 m²',
  '80_to_120': '80–120 m²',
  '120_to_160': '120–160 m²',
}

export function getIlmanvaihtoBookingData(
  formValue: Record<string, unknown>,
): ServiceBookingData {
  const {
    apartmentSize: size,
    floors,
    ventilationType: ventType,
    includeAirflowMeasurement = false,
    includeCookerHoodCleaning: hasCookerHood = false,
  } = formValue as IlmanvaihtoFormValue
  // Measurement add-on is only valid for non-natural ventilation
  const hasMeasurement = includeAirflowMeasurement && ventType !== 'natural'

  // Main task price = base + multi-floor surcharge folded in (no separate task)
  const mainTaskPrice =
    IV_CLEANING_PRICES[size][ventType] +
    (floors === 'multiple' ? IV_MULTI_FLOOR_SURCHARGE : 0)

  const tasks: ServiceTaskLine[] = [
    { taskId: IV_TASK_IDS[ventType], price: mainTaskPrice },
  ]
  if (hasMeasurement) {
    tasks.push({
      taskId: IV_TASK_IDS.measurement,
      price: IV_MEASUREMENT_PRICES[size],
    })
  }
  if (hasCookerHood) {
    tasks.push({
      taskId: IV_TASK_IDS.cookerHood,
      price: IV_COOKER_HOOD_PRICE,
    })
  }

  // Duration
  let duration = IV_DURATION[size][ventType]
  if (floors === 'multiple') duration += IV_DURATION_MULTI_FLOOR
  if (hasMeasurement)
    duration +=
      IV_DURATION_MEASUREMENT[size][
        ventType as 'mechanical_exhaust' | 'heat_recovery'
      ]
  if (hasCookerHood) duration += IV_DURATION_COOKER_HOOD

  const totalCost = sumBy(tasks, (t) => t.price)

  // Service details string (compact, single line — used in emails and internal notes)
  const parts = [
    `IV-puhdistus, ${IV_TYPE_LABELS[ventType]}, ${IV_SIZE_LABELS[size]}`,
  ]
  if (floors === 'multiple') parts.push('useampi kerros')
  if (hasMeasurement) parts.push('mittaus ja säätö')
  if (hasCookerHood) parts.push('liesituuletin')

  return {
    tasks,
    serviceDetails: `${parts.join(', ')} - ${totalCost} €`,
    estimatedDurationMinutes: duration,
    estimatedCost: totalCost,
  }
}

/** Registry of all service presets */
export const SERVICE_PRESET_DATA: Record<ServicePresetKey, ServicePresetData> =
  {
    nuohous: {
      id: 'nuohous',
      name: 'Nuohous',
      description: 'Hormin ja tulisijan nuohous',
      maxDrivingMinutes: 15,
      minBookingAdvanceHours: 4,
      slotSpacingMinutes: 45,
      notificationEmails: ['info@markku.fi'],
      defaultFormValue: {
        fireplaces: 1,
        hasBoiler: false,
      } satisfies NuohousFormValue as Record<string, unknown>,
      getBookingData: getNuohousBookingData,
    },
    ilmanvaihto: {
      id: 'ilmanvaihto',
      name: 'IV-kanavien puhdistus',
      description: 'Ilmanvaihtokanavien puhdistus',
      maxDrivingMinutes: 60,
      minBookingAdvanceHours: 16,
      slotSpacingMinutes: 240,
      notificationEmails: ['info@markku.fi', 'jani.issakainen@markku.fi'],
      defaultFormValue: {
        apartmentSize: 'under_80',
        floors: 'single',
        ventilationType: 'natural',
        includeAirflowMeasurement: false,
        includeCookerHoodCleaning: false,
      } satisfies IlmanvaihtoFormValue as Record<string, unknown>,
      getBookingData: getIlmanvaihtoBookingData,
    },
  }
